import { useCallback, useEffect, useState } from "react";
import api, { fmtIDR, apiErr } from "@/lib/api";
import { DateInput, fmtDate } from "@/components/DateInput";
import { useI18n } from "@/context/I18nContext";
import { fl } from "@/lib/finI18n";
import { ComboBox } from "@/components/ComboBox";
import { useTableTools, TableToolbar, SelectAllBox, SelectBox, deleteEach } from "@/components/TableTools";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Factory, X } from "lucide-react";

const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const num = (v) => Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 4 });

export function ProductionTab({ clientId, products, onDone }) {
  const { lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    if (!clientId) return;
    const { data } = await api.get(`/productions?client_id=${clientId}`);
    setRows(data);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const tools = useTableTools(rows);
  const cols = [["No", "no"], ["Tanggal", (p) => fmtDate(p.date)], [fl("finishedGood", lang), "product_name"], [fl("qty", lang), "qty"], ["Satuan", "unit"],
    [fl("materialCost", lang), "material_cost"], [fl("extraCost", lang), "extra_cost"], [fl("totalCost", lang), "total_cost"], [fl("unitCost", lang), "unit_cost"]];
  const delMany = async (ids) => { const r = await deleteEach(ids, (id) => api.delete(`/productions/${id}`)); load(); onDone(); return r; };

  const del = async (p) => {
    if (!window.confirm(`Hapus produksi ${p.no}? Stok & jurnal akan dibatalkan.`)) return;
    try {
      const { data } = await api.delete(`/productions/${p.id}`);
      toast.success("Produksi dihapus");
      if (data?.recalculated) toast.info(`${data.recalculated} ${fl("recalcToast", lang)}`);
      load(); onDone();
    }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <Card className="p-4" data-testid="production-tab">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="font-head font-semibold flex items-center gap-2"><Factory className="h-4 w-4" />{fl("prodTitle", lang)} <span className="text-muted-foreground font-normal text-sm">({rows.length})</span></h2>
          <p className="text-xs text-muted-foreground">{fl("prodDesc", lang)}</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="production-new-btn"><Plus className="h-4 w-4 mr-1.5" />{fl("prodNew", lang)}</Button>
      </div>
      <div className="mb-3"><TableToolbar tools={tools} id="production" cols={cols} filename="produksi" onDelete={delMany} /></div>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50">
            <TableHead className="w-8"><SelectAllBox tools={tools} id="production" /></TableHead>
            <TableHead>{fl("prodNo", lang)}</TableHead><TableHead>{fl("date", lang)}</TableHead><TableHead>{fl("finishedGood", lang)}</TableHead>
            <TableHead className="text-right">{fl("qty", lang)}</TableHead><TableHead className="text-right">{fl("materialCost", lang)}</TableHead>
            <TableHead className="text-right">{fl("extraCost", lang)}</TableHead><TableHead className="text-right">{fl("totalCost", lang)}</TableHead>
            <TableHead className="text-right">{fl("unitCost", lang)}</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {tools.view.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground text-sm">{fl("empty", lang)}</TableCell></TableRow>}
            {tools.view.map((p) => (
              <TableRow key={p.id} data-testid={`production-row-${p.id}`}>
                <TableCell><SelectBox tools={tools} row={p} id="production" /></TableCell>
                <TableCell className="text-xs font-medium whitespace-nowrap">{p.no}</TableCell>
                <TableCell className="num text-xs">{fmtDate(p.date)}</TableCell>
                <TableCell className="text-sm">{p.product_name}
                  <span className="block text-[11px] text-muted-foreground">{p.materials.map((m) => `${m.name} ${num(m.qty)} ${m.unit}`).join(" · ")}</span>
                </TableCell>
                <TableCell className="num text-right text-xs">{num(p.qty)} {p.unit}</TableCell>
                <TableCell className="num text-right text-xs">{fmtIDR(p.material_cost)}</TableCell>
                <TableCell className="num text-right text-xs">{fmtIDR(p.extra_cost)}</TableCell>
                <TableCell className="num text-right text-xs font-semibold">{fmtIDR(p.total_cost)}</TableCell>
                <TableCell className="num text-right text-xs">{fmtIDR(p.unit_cost)}
                  {p.recalculated_at && <span className="block text-[10px] text-amber-600" title={p.recalculated_at} data-testid={`production-recalc-${p.id}`}>{fl("recalcBadge", lang)}</span>}
                </TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(p)} data-testid={`production-del-${p.id}`}><Trash2 className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {open && <ProductionForm clientId={clientId} products={products} onClose={() => setOpen(false)} onDone={() => { load(); onDone(); }} />}
    </Card>
  );
}

function ProductionForm({ clientId, products, onClose, onDone }) {
  const { lang } = useI18n();
  const stockable = products.filter((p) => p.track_stock);
  const byId = Object.fromEntries(stockable.map((p) => [p.id, p]));
  const [f, setF] = useState({ date: today(), fgNew: false, product_id: "", product_name: "", unit: "", qty: "", note: "",
    materials: [{ product_id: "", qty: "" }], costs: [{ description: "", amount: "" }] });
  const [saving, setSaving] = useState(false);
  const [priceAt, setPriceAt] = useState({});
  useEffect(() => {
    if (clientId && /^\d{4}-\d{2}-\d{2}$/.test(f.date)) api.get(`/inventory/costs?client_id=${clientId}&at=${f.date}`).then(({ data }) => setPriceAt(data)).catch(() => setPriceAt({}));
  }, [clientId, f.date]);
  const costOf = (pid) => Number(priceAt[pid] ?? byId[pid]?.avg_cost ?? 0);
  const setArr = (k, i, patch) => setF((s) => ({ ...s, [k]: s[k].map((x, idx) => (idx === i ? { ...x, ...patch } : x)) }));

  const matCost = f.materials.reduce((s, m) => s + Math.round((Number(m.qty) || 0) * costOf(m.product_id) * 100) / 100, 0);
  const extra = f.costs.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const total = Math.round((matCost + extra) * 100) / 100;
  const unitCost = Number(f.qty) > 0 ? total / Number(f.qty) : 0;
  const fgOpts = stockable.filter((p) => !f.materials.some((m) => m.product_id === p.id)).map((p) => ({ value: p.id, label: `${p.name} (${fl(`cat_${p.category || "goods"}`, lang)})` }));
  const matOpts = stockable.filter((p) => p.id !== f.product_id)
    .sort((a, b) => (a.category === "material" ? 0 : 1) - (b.category === "material" ? 0 : 1) || a.name.localeCompare(b.name))
    .map((p) => ({ value: p.id, label: `${p.name} (${fl(`cat_${p.category || "goods"}`, lang)}) · ${fl("stockAvail", lang)} ${num(p.stock)} ${p.unit || ""} · ${fmtIDR(costOf(p.id))}` }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/productions", {
        client_id: clientId, date: f.date, qty: Number(f.qty || 0), note: f.note,
        product_id: f.fgNew ? "" : f.product_id, product_name: f.fgNew ? f.product_name : "", unit: f.fgNew ? f.unit : "",
        materials: f.materials.filter((m) => m.product_id).map((m) => ({ product_id: m.product_id, qty: Number(m.qty || 0) })),
        costs: f.costs.filter((c) => Number(c.amount)).map((c) => ({ description: c.description, amount: Number(c.amount) })),
      });
      toast.success("Produksi dicatat — stok barang jadi bertambah");
      if (data.recalculated) toast.info(`${data.recalculated} ${fl("recalcToast", lang)}`);
      onClose(); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="production-form">
        <DialogHeader><DialogTitle>{fl("prodNew", lang)}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>{fl("date", lang)}</Label><DateInput value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="production-date" /></div>
            <div className="sm:col-span-2 space-y-1.5">
              <div className="flex items-center justify-between"><Label>{fl("finishedGood", lang)}</Label>
                <button type="button" className="text-[11px] text-primary hover:underline" onClick={() => setF({ ...f, fgNew: !f.fgNew })} data-testid="production-fg-toggle">{f.fgNew ? fl("pickFg", lang) : `+ ${fl("newFg", lang)}`}</button>
              </div>
              {f.fgNew ? (
                <div className="grid grid-cols-3 gap-2">
                  <Input className="col-span-2" value={f.product_name} onChange={(e) => setF({ ...f, product_name: e.target.value })} placeholder={fl("itemName", lang)} data-testid="production-fg-name" />
                  <Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder={fl("unit", lang)} data-testid="production-fg-unit" />
                </div>
              ) : (
                <ComboBox testid="production-fg" value={f.product_id} onChange={(v) => setF({ ...f, product_id: v })} placeholder={fl("pickFg", lang)} searchPlaceholder={fl("itemName", lang)} options={fgOpts} />
              )}
            </div>
          </div>
          <div className="space-y-1.5 max-w-xs"><Label>{fl("qtyOut", lang)}</Label><Input type="number" min="0" className="num" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="production-qty" /></div>

          <div className="rounded-md border border-border">
            <div className="flex items-center justify-between px-3 py-2 bg-secondary/40 border-b border-border">
              <span className="text-sm font-medium">{fl("materialsUsed", lang)}<span className="block text-[11px] font-normal text-muted-foreground" data-testid="production-cost-basis">{fl("costAtDate", lang)}</span></span>
              <Button type="button" size="sm" variant="outline" onClick={() => setF({ ...f, materials: [...f.materials, { product_id: "", qty: "" }] })} data-testid="production-add-material"><Plus className="h-3.5 w-3.5 mr-1" />{fl("addMaterial", lang)}</Button>
            </div>
            {f.materials.map((m, i) => {
              const p = byId[m.product_id];
              const short = p && Number(m.qty) > Number(p.stock || 0);
              return (
                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-b border-border/60 last:border-0" data-testid={`production-material-${i}`}>
                  <div className="col-span-12 sm:col-span-6"><ComboBox testid={`production-material-product-${i}`} value={m.product_id} onChange={(v) => setArr("materials", i, { product_id: v })} placeholder={fl("pickMaterial", lang)} searchPlaceholder={fl("itemName", lang)} options={matOpts} /></div>
                  <Input type="number" min="0" className="col-span-4 sm:col-span-2 num h-9" placeholder={fl("qty", lang)} value={m.qty} onChange={(e) => setArr("materials", i, { qty: e.target.value })} data-testid={`production-material-qty-${i}`} />
                  <span className={`col-span-6 sm:col-span-3 text-xs num text-right ${short ? "text-rose-600" : "text-muted-foreground"}`} data-testid={`production-material-cost-${i}`}>
                    {short ? `${fl("insufficient", lang)} (${num(p.stock)})` : fmtIDR((Number(m.qty) || 0) * costOf(m.product_id))}
                  </span>
                  <div className="col-span-2 sm:col-span-1 flex justify-end">{f.materials.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setF({ ...f, materials: f.materials.filter((_, idx) => idx !== i) })} data-testid={`production-material-del-${i}`}><X className="h-4 w-4" /></Button>}</div>
                </div>
              );
            })}
          </div>

          <div className="rounded-md border border-border">
            <div className="flex items-center justify-between px-3 py-2 bg-secondary/40 border-b border-border">
              <span className="text-sm font-medium">{fl("extraCosts", lang)}</span>
              <Button type="button" size="sm" variant="outline" onClick={() => setF({ ...f, costs: [...f.costs, { description: "", amount: "" }] })} data-testid="production-add-cost"><Plus className="h-3.5 w-3.5 mr-1" />{fl("addCost", lang)}</Button>
            </div>
            {f.costs.map((c, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-b border-border/60 last:border-0" data-testid={`production-cost-${i}`}>
                <Input className="col-span-12 sm:col-span-7 h-9" placeholder={fl("costDesc", lang)} value={c.description} onChange={(e) => setArr("costs", i, { description: e.target.value })} data-testid={`production-cost-desc-${i}`} />
                <Input type="number" min="0" className="col-span-10 sm:col-span-4 num text-right h-9" placeholder={fl("amount", lang)} value={c.amount} onChange={(e) => setArr("costs", i, { amount: e.target.value })} data-testid={`production-cost-amount-${i}`} />
                <div className="col-span-2 sm:col-span-1 flex justify-end">{f.costs.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setF({ ...f, costs: f.costs.filter((_, idx) => idx !== i) })} data-testid={`production-cost-del-${i}`}><X className="h-4 w-4" /></Button>}</div>
              </div>
            ))}
          </div>

          <div className="space-y-1.5"><Label>{fl("note", lang)}</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="production-note" /></div>

          <div className="rounded-md bg-secondary/40 p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="production-summary">
            <div><p className="text-[11px] text-muted-foreground">{fl("materialCost", lang)}</p><p className="num font-medium" data-testid="production-sum-material">{fmtIDR(matCost)}</p></div>
            <div><p className="text-[11px] text-muted-foreground">{fl("extraCost", lang)}</p><p className="num font-medium" data-testid="production-sum-extra">{fmtIDR(extra)}</p></div>
            <div><p className="text-[11px] text-muted-foreground">{fl("totalCost", lang)}</p><p className="num font-semibold" data-testid="production-sum-total">{fmtIDR(total)}</p></div>
            <div><p className="text-[11px] text-muted-foreground">{fl("unitCost", lang)}</p><p className="num font-semibold" data-testid="production-sum-unit">{fmtIDR(unitCost)}</p></div>
          </div>
          <p className="text-[11px] text-muted-foreground">{fl("prodJournal", lang)}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{fl("cancel", lang)}</Button>
          <Button onClick={save} disabled={saving} data-testid="production-save">{fl("prodNew", lang)}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
